// Comments on a document artifact (#13194): the one read of an artifact's
// comment threads, and every write to them. The list is a react-query answer
// keyed by artifact id (rules/frontend.md, module state rule 2); every write
// this client makes invalidates it, and so does a push from the server: the
// workspace room's WORKSPACE_COMMENTS_UPDATE (dispatched by the socket
// consumer as the `documentCommentsChanged` window event) and a chat write
// into the document (`editorDocumentRefresh`).
import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import fpFetch from "../../services/fpFetch";
import { BASE_API_URL_V2 } from "../../constants";

/** The five reactions, stored as their emoji. */
export const COMMENT_REACTIONS = ["👍", "✅", "👀", "❓", "⏳"] as const;
export type CommentReactionEmoji = (typeof COMMENT_REACTIONS)[number];

/** Someone who gave a reaction. */
export interface CommentReactor {
  user_id: string;
  name: string;
}

export interface CommentReaction {
  emoji: string;
  count: number;
  /** Whether the caller reacted with it. */
  mine: boolean;
  /** Who reacted, in the order they did. */
  reactors: CommentReactor[];
}

export interface DocumentComment {
  id: string;
  author_kind: "person" | "agent";
  author_user_id: string | null;
  author_name: string;
  /** Plain text; newlines kept. */
  body: string;
  created_at: string;
  edited_at: string | null;
  /** Whether the caller wrote it (and so may edit or delete it). */
  mine: boolean;
  /** Whether its author sent it to the document's agent. */
  sent_to_agent: boolean;
  reactions: CommentReaction[];
}

export interface DocumentCommentThread {
  id: string;
  quote: string;
  /** Whether the stored body still carries the thread's mark. */
  in_document: boolean;
  addressed_to_agent: boolean;
  /** The agent is answering a comment sent to it here: the turn the comment
   *  woke is running (or waiting behind one), and it has not replied yet. */
  agent_working: boolean;
  resolved_at: string | null;
  resolved_by_name: string | null;
  created_at: string;
  /** Oldest first; the first is the thread's opening comment. */
  comments: DocumentComment[];
}

export interface DocumentCommentList {
  threads: DocumentCommentThread[];
  open_count: number;
  workspace_id: string;
  /** The name the document's agent goes by ("Finpilot", or a named
   *  agent's); null when no conversation is behind the document. */
  agent_name?: string | null;
  /** Whether the caller may send a comment to that agent. */
  can_send_to_agent?: boolean;
}

export const documentCommentsKey = (artifactId: string) =>
  ["artifactComments", artifactId] as const;

const commentsPath = (artifactId: string, rest = ""): string =>
  `${BASE_API_URL_V2}/api/v2/artifacts/${artifactId}/comments${rest}`;

const call = async <T>(
  url: string,
  init: { method: string; body?: unknown; signal?: AbortSignal }
): Promise<T> => {
  const response = await fpFetch(url, {
    "x-finpilot-auth": true,
    method: init.method,
    signal: init.signal,
    headers: { "Content-Type": "application/json" },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
};

export const fetchDocumentComments = (
  artifactId: string,
  signal?: AbortSignal
): Promise<DocumentCommentList> =>
  call<DocumentCommentList>(commentsPath(artifactId), {
    method: "GET",
    signal,
  });

const emojiPath = (emoji: string): string => encodeURIComponent(emoji);

export interface DocumentCommentActions {
  create: (input: {
    quote: string;
    body: string;
    send_to_agent?: boolean;
  }) => Promise<DocumentCommentThread>;
  /** A reply; `sendToAgent` sends it to the document's agent as well. */
  reply: (
    threadId: string,
    body: string,
    sendToAgent?: boolean
  ) => Promise<DocumentComment>;
  resolve: (threadId: string) => Promise<DocumentCommentThread>;
  reopen: (threadId: string) => Promise<DocumentCommentThread>;
  edit: (
    threadId: string,
    commentId: string,
    body: string
  ) => Promise<DocumentComment>;
  remove: (threadId: string, commentId: string) => Promise<void>;
  react: (
    threadId: string,
    commentId: string,
    emoji: CommentReactionEmoji
  ) => Promise<DocumentComment>;
  unreact: (
    threadId: string,
    commentId: string,
    emoji: CommentReactionEmoji
  ) => Promise<DocumentComment>;
}

export interface UseDocumentComments extends DocumentCommentActions {
  /** In creation order; empty while loading. */
  threads: DocumentCommentThread[];
  openCount: number;
  loaded: boolean;
  /** The document's agent's name ("Finpilot", or a named agent's), for
   *  every reader; null when no conversation is behind the document. */
  agentName: string | null;
  /** That name when the caller may send a comment to the agent (the
   *  document's creator); null otherwise. */
  sendToAgentAs: string | null;
}

const EMPTY: DocumentCommentThread[] = [];

/** An artifact's comment threads and the writes to them. Mount it once per
 *  host and hand the answer down: every mounted copy listens for the
 *  refresh events. */
export const useDocumentComments = (
  artifactId: string | null | undefined,
  workspaceId: string | null | undefined
): UseDocumentComments => {
  const queryClient = useQueryClient();
  const id = artifactId ?? "";
  const query = useQuery({
    queryKey: documentCommentsKey(id),
    queryFn: ({ signal }) => fetchDocumentComments(id, signal),
    enabled: Boolean(artifactId),
    retry: false,
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: documentCommentsKey(id) }),
    [queryClient, id]
  );

  // A colleague's comment, the agent's reply, or a chat write into the
  // document: the server says so on the workspace room, and the list
  // refetches.
  useEffect(() => {
    if (!artifactId || !workspaceId) return;
    const onEvent = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        workspaceId?: unknown;
      } | null;
      if (detail?.workspaceId !== workspaceId) return;
      void invalidate();
    };
    window.addEventListener("documentCommentsChanged", onEvent);
    window.addEventListener("editorDocumentRefresh", onEvent);
    return () => {
      window.removeEventListener("documentCommentsChanged", onEvent);
      window.removeEventListener("editorDocumentRefresh", onEvent);
    };
  }, [artifactId, workspaceId, invalidate]);

  const actions = useMemo((): DocumentCommentActions => {
    const write = async <T>(
      rest: string,
      method: string,
      body?: unknown
    ): Promise<T> => {
      try {
        return await call<T>(commentsPath(id, rest), { method, body });
      } finally {
        void invalidate();
      }
    };
    const message = (threadId: string, commentId: string): string =>
      `/${threadId}/messages/${commentId}`;
    return {
      create: (input) => write("", "POST", input),
      reply: (threadId, body, sendToAgent) =>
        write(
          `/${threadId}/replies`,
          "POST",
          sendToAgent ? { body, send_to_agent: true } : { body }
        ),
      resolve: (threadId) => write(`/${threadId}/resolve`, "POST"),
      reopen: (threadId) => write(`/${threadId}/reopen`, "POST"),
      edit: (threadId, commentId, body) =>
        write(message(threadId, commentId), "PATCH", { body }),
      remove: (threadId, commentId) =>
        write(message(threadId, commentId), "DELETE"),
      react: (threadId, commentId, emoji) =>
        write(
          `${message(threadId, commentId)}/reactions/${emojiPath(emoji)}`,
          "PUT"
        ),
      unreact: (threadId, commentId, emoji) =>
        write(
          `${message(threadId, commentId)}/reactions/${emojiPath(emoji)}`,
          "DELETE"
        ),
    };
  }, [id, invalidate]);

  const threads = query.data?.threads ?? EMPTY;
  const openCount = useMemo(
    () => threads.filter((t) => t.resolved_at === null).length,
    [threads]
  );
  const agentName = query.data?.agent_name ?? null;
  return {
    ...actions,
    threads,
    openCount,
    loaded: query.data !== undefined,
    agentName,
    sendToAgentAs: query.data?.can_send_to_agent ? agentName : null,
  };
};
